import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, MousePointer, TrendingUp, Calendar } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useAnalytics, useProducts } from '../hooks/useQueries';

export function AnalyticsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: analytics, isLoading: analyticsLoading } = useAnalytics(user?.id || '');
  const { data: products = [], isLoading: productsLoading } = useProducts(user?.id || '');

  if (analyticsLoading || productsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const totalProductViews = products.reduce((sum, product) => sum + product.analytics.views, 0);
  const totalProductClicks = products.reduce((sum, product) => sum + product.analytics.clicks, 0);
  const clickThroughRate = totalProductViews > 0 ? ((totalProductClicks / totalProductViews) * 100).toFixed(1) : '0';

  const last7Days = analytics?.dailyViews.slice(0, 7) || [];
  const weeklyViews = last7Days.reduce((sum, day) => sum + day.views, 0);
  const weeklyClicks = last7Days.reduce((sum, day) => sum + day.clicks, 0);

  const topProducts = products
    .sort((a, b) => (b.analytics.clicks + b.analytics.views) - (a.analytics.clicks + a.analytics.views))
    .slice(0, 5);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Overview</h1>
          <p className="text-gray-600 mt-2">Track your profile and product performance</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Profile Views</p>
                <p className="text-2xl font-bold text-gray-900">{analytics?.totalViews || 0}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Clicks</p>
                <p className="text-2xl font-bold text-gray-900">{analytics?.totalClicks || 0}</p>
              </div>
              <MousePointer className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Click-Through Rate</p>
                <p className="text-2xl font-bold text-gray-900">{clickThroughRate}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Products</p>
                <p className="text-2xl font-bold text-gray-900">{products.length}</p>
              </div>
              <Calendar className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Weekly Overview */}
        <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Last 7 Days</h2>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
            {last7Days.map((day, index) => (
              <div key={day.date} className="text-center">
                <div className="text-xs text-gray-500 mb-1">
                  {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="text-lg font-bold text-blue-600">{day.views}</div>
                  <div className="text-xs text-gray-600">views</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 mt-1">
                  <div className="text-lg font-bold text-green-600">{day.clicks}</div>
                  <div className="text-xs text-gray-600">clicks</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{weeklyViews}</div>
                <div className="text-sm text-gray-600">Total Weekly Views</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{weeklyClicks}</div>
                <div className="text-sm text-gray-600">Total Weekly Clicks</div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Top Performing Products</h2>
          <div className="space-y-4">
            {topProducts.length > 0 ? (
              topProducts.map((product, index) => (
                <div key={product.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-blue-600">#{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{product.name}</h3>
                      <p className="text-sm text-gray-600">Product #{product.productNumber}</p>
                    </div>
                  </div>
                  <div className="flex space-x-6 text-sm">
                    <div className="text-center">
                      <div className="font-bold text-blue-600">{product.analytics.views}</div>
                      <div className="text-gray-600">Views</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-green-600">{product.analytics.clicks}</div>
                      <div className="text-gray-600">Clicks</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-purple-600">
                        {product.analytics.views > 0 ? 
                          ((product.analytics.clicks / product.analytics.views) * 100).toFixed(1) : 
                          '0'
                        }%
                      </div>
                      <div className="text-gray-600">CTR</div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No products yet. Create your first product to see analytics!</p>
              </div>
            )}
          </div>
        </div>

        {/* Social Links Performance */}
        {analytics && Object.keys(analytics.socialLinkClicks).length > 0 && (
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Social Links Performance</h2>
            <div className="space-y-3">
              {Object.entries(analytics.socialLinkClicks)
                .sort(([,a], [,b]) => b - a)
                .map(([linkId, clicks]) => (
                  <div key={linkId} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-700">Social Link {linkId.slice(-4)}</span>
                    <span className="font-bold text-blue-600">{clicks} clicks</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
